import React, { useState, useEffect, useCallback } from 'react';
import './TabPeso.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';

const BASE = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.PESOS;

const EMPTY = {
  fecha: new Date().toISOString().split('T')[0],
  peso: '',
  observaciones: '',
};

export default function TabPeso({ animalId, onPesoActualizado }) {
  const [registros, setRegistros]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando]       = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [guardando, setGuardando]     = useState(false);
  const [error, setError]             = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/animal/${animalId}`);
      if (!res.ok) throw new Error('Error al cargar registros de peso');
      setRegistros(await res.json());
    } catch (e) {
      console.error(e);
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(EMPTY);
    setError('');
    setMostrarForm(true);
  };

  const abrirEditar = (r) => {
    setEditando(r.id);
    setForm({
      fecha:         r.fecha         || '',
      peso:          r.peso          || '',
      observaciones: r.observaciones || '',
    });
    setError('');
    setMostrarForm(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.fecha)  return setError('La fecha es obligatoria.');
    if (!form.peso)   return setError('El peso es obligatorio.');

    setGuardando(true); setError('');
    try {
      const payload = { ...form, peso: parseFloat(form.peso), animalId: parseInt(animalId) };
      const url    = editando ? `${BASE}/${editando}` : BASE;
      const method = editando ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Error al guardar');
      }
      await cargar();
      setMostrarForm(false);
      setEditando(null);
      if (onPesoActualizado) onPesoActualizado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este registro de peso?')) return;
    try {
      await apiFetch(`${BASE}/${id}`, { method: 'DELETE' });
      await cargar();
      if (onPesoActualizado) onPesoActualizado();
    } catch (e) {
      alert('Error al eliminar');
    }
  };

  const formatFecha = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-PY') : '-';

  const pesoActual = registros.length > 0 ? registros[0].peso : null;

  return (
    <div className="tab-peso">
      <div className="tab-toolbar">
        <div>
          <h3>⚖️ Historial de Pesajes</h3>
          {pesoActual && (
            <span className="peso-actual-badge">Último: <strong>{pesoActual} kg</strong></span>
          )}
        </div>
        <button className="btn-primary btn-sm" onClick={abrirNuevo}>+ Registrar Peso</button>
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : registros.length === 0 ? (
        <div className="empty-state">
          <p>⚖️ No hay registros de peso para este animal.</p>
          <button className="btn-primary btn-sm" onClick={abrirNuevo}>Registrar primer pesaje</button>
        </div>
      ) : (
        <table className="peso-tabla">
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha</th>
              <th>Peso (kg)</th>
              <th>Variación</th>
              <th>Observaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.map((r, idx) => {
              const anterior = registros[idx + 1];
              const variacion = anterior ? r.peso - anterior.peso : null;
              return (
                <tr key={r.id} className={idx === 0 ? 'fila-ultimo' : ''}>
                  <td className="idx-col">
                    {idx === 0 && <span className="badge-ultimo">Último</span>}
                    {idx > 0 && <span className="num-col">{registros.length - idx}</span>}
                  </td>
                  <td><strong>{formatFecha(r.fecha)}</strong></td>
                  <td className="peso-col"><strong>{r.peso}</strong> kg</td>
                  <td>
                    {variacion !== null ? (
                      <span className={`badge-variacion ${variacion >= 0 ? 'sube' : 'baja'}`}>
                        {variacion >= 0 ? '▲' : '▼'} {Math.abs(variacion).toFixed(1)} kg
                      </span>
                    ) : (
                      <span className="sin-variacion">—</span>
                    )}
                  </td>
                  <td className="obs-col">{r.observaciones || '-'}</td>
                  <td>
                    <div className="acciones">
                      <button className="btn-info btn-sm" onClick={() => abrirEditar(r)}>✏️</button>
                      <button className="btn-danger btn-sm" onClick={() => eliminar(r.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Formulario modal */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setMostrarForm(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h2>{editando ? 'Editar Registro' : 'Registrar Pesaje'}</h2>
              <button className="modal-close" onClick={() => setMostrarForm(false)}>✕</button>
            </div>
            <form onSubmit={guardar}>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-grid">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input type="date" value={form.fecha}
                         onChange={e => setForm(p => ({...p, fecha: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label>Peso (kg) *</label>
                  <input type="number" value={form.peso} step="0.1" min="0"
                         onChange={e => setForm(p => ({...p, peso: e.target.value}))}
                         placeholder="Ej: 350" required
                         style={{fontSize:'1.2rem'}} />
                </div>
                <div className="form-group full">
                  <label>Observaciones</label>
                  <textarea value={form.observaciones}
                            onChange={e => setForm(p => ({...p, observaciones: e.target.value.toUpperCase()}))}
                            rows={2} placeholder="Notas del pesaje..."
                            style={{textTransform:'uppercase'}} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarForm(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : '✓ Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
